import { useCallback, useEffect, useState } from "react";
import { getApiKey, setApiKey } from "@/lib/auth";
import { apiFetch, clearAppPassword, setAppPassword } from "@/lib/backend";
import { useDismiss } from "@/lib/hooks";

type Phase = "checking" | "password" | "llm-key" | "ready";

async function checkAccess(): Promise<Phase> {
  try {
    const r = await apiFetch("/api/llm-key");
    if (r.status === 401) return "password";
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      return d.hasKey ? "ready" : "llm-key";
    }
    return "llm-key";
  } catch {
    return "llm-key";
  }
}

function normalizePassword(raw: string): string {
  return raw.replace(/[\u200B-\u200D\uFEFF]/g, "").trim();
}

function PasswordForm({ onSuccess }: { onSuccess: () => void }) {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizePassword(value);
    if (!normalized) return;
    setError(null);
    setTesting(true);
    setAppPassword(normalized);
    const r = await apiFetch("/api/llm-key");
    setTesting(false);
    if (r.status === 401) {
      clearAppPassword();
      setError("Incorrect password.");
      return;
    }
    onSuccess();
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          autoFocus
          autoComplete="current-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (text) {
              e.preventDefault();
              setValue(text);
            }
          }}
          placeholder="Password"
          className="w-full rounded-md border bg-background px-3 py-2 pr-16 text-sm outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-0 px-3 text-xs text-muted-foreground hover:text-foreground"
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <button
        type="submit"
        disabled={!normalizePassword(value) || testing}
        className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {testing ? "Checking…" : "Continue"}
      </button>
    </form>
  );
}

async function saveApiKey(key: string): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await apiFetch("/api/llm-key", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    });
    if (res.ok) {
      setApiKey(key);
      return { ok: true };
    }
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.error ?? `Server responded ${res.status}` };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

type ApiKeyFormProps = {
  initialValue?: string;
  submitLabel: string;
  skipTestIfUnchanged?: boolean;
  onSaved: () => void;
  onCancel?: () => void;
};

function ApiKeyForm({ initialValue = "", submitLabel, skipTestIfUnchanged, onSaved, onCancel }: ApiKeyFormProps) {
  const [value, setValue] = useState(initialValue);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setError(null);

    if (skipTestIfUnchanged && trimmed === initialValue) {
      onSaved();
      return;
    }

    setTesting(true);
    const result = await saveApiKey(trimmed);
    setTesting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onSaved();
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <input
        type="text"
        autoFocus
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPaste={(e) => {
          const text = e.clipboardData.getData("text");
          if (text) {
            e.preventDefault();
            setValue(text);
          }
        }}
        placeholder="sk-..."
        className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!value.trim() || testing}
          className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {testing ? "Testing…" : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export function ApiKeyGate({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>("checking");

  const refresh = useCallback(() => {
    setPhase("checking");
    checkAccess().then(setPhase);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (phase === "checking") {
    return <div className="flex min-h-dvh items-center justify-center text-xs text-muted-foreground">Loading…</div>;
  }
  if (phase === "ready") return <>{children}</>;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4">
      <div className="w-full max-w-sm flex flex-col gap-4">
        {phase === "password" ? (
          <>
            <div>
              <h1 className="text-lg font-semibold">Libre LM Studio</h1>
              <p className="text-sm text-muted-foreground">Enter the password to continue.</p>
            </div>
            <PasswordForm onSuccess={refresh} />
          </>
        ) : (
          <>
            <div>
              <h1 className="text-lg font-semibold">Libre LM Studio</h1>
              <p className="text-sm text-muted-foreground">Enter your API key to continue. Stored locally in your browser.</p>
            </div>
            <ApiKeyForm submitLabel="Continue" onSaved={() => setPhase("ready")} />
          </>
        )}
      </div>
    </div>
  );
}

export function ApiKeyDialog({ onClose }: { onClose: () => void }) {
  useDismiss(onClose);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-lg border bg-card p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">API Key</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">Stored locally in your browser. Saving will test the key against the server.</p>
        <ApiKeyForm
          initialValue={getApiKey() ?? ""}
          submitLabel="Save"
          skipTestIfUnchanged
          onSaved={onClose}
          onCancel={onClose}
        />
      </div>
    </div>
  );
}
